import { Input } from 'antd';
import { $Formutil, connect, FormItem, FormItemComponentProps } from 'react-antd-formutil';
import FieldSelect, { FieldSelectProps } from 'components/FieldSelect';
import { Collapse } from 'components/Transition';
import arrowBottom from 'static/images/icons/arrow-bottom.svg';
import iconLock from 'static/images/icons/icon-lock.svg';
import APP from 'stores/APP';
import classlist from 'utils/classlist';
import { getCurrencyInfo } from 'utils/util';

import './style.scss';

function isPrice(num: string | number) {
    return /^(([1-9][0-9]*)|(([0]\.\d{1,2}|[1-9][0-9]*\.\d{1,2})))$/.test(String(num).replace(/,/g, ''));
}

function formatPrice(str, decimal?) {
    if (typeof decimal === 'number') {
        str = (str * 1).toFixed(decimal);
    }

    return String(str).replace(/\d+(?:\.\d+)*/g, function(price) {
        const arr = price.split('.');

        if (arr.length > 2) {
            return price;
        }

        arr[0] = arr[0].replace(/(\d{1,3})(?=(\d{3})+$)/g, '$1,');

        return arr.join('.');
    });
}

interface CurrencyAmountProps {
    inputConfig?: Partial<Omit<FormItemComponentProps, 'help' | 'errorLevel'> & { placeholder: string }>;
    selectConfig?: Omit<
        FormItemComponentProps & { disabled?: boolean; showSearch?: boolean; showIcon?: boolean },
        'children' | 'help' | 'errorLevel'
    >;
    disabled?: boolean;
    currencyList?: FieldSelectProps['data'];
    className?: string;
    $formutil: $Formutil;
    help?: React.ReactNode;
    errorLevel?: FormItemComponentProps['errorLevel'];
    showExplain?: boolean; // 是否显示说明信息 （error 和 help）与 errorLevel = off 搭配使用 默认值为 true
    extra?: FormItemComponentProps['extra'];
}

const CurrencyAmount: React.FC<CurrencyAmountProps> = ({
    className,
    inputConfig = {},
    selectConfig = {},
    currencyList,
    $formutil,
    showExplain = true,
    help,
    errorLevel = 'off',
    disabled,
    extra
}) => {
    const { className: inputClassName, placeholder, checker, ...otherInputConfig } = inputConfig;
    const {
        disabled: selectDisabled,
        className: selectClassName,
        showSearch,
        showIcon = true,
        ...otherSelectConfig
    } = selectConfig;

    const { $errors, $getField, $params, $invalid, $dirts } = $formutil.$new();
    const firstError =
        !!otherInputConfig.name &&
        otherInputConfig.name in $errors &&
        $getField(otherInputConfig.name)?.$getFirstError();
    const isDirty = !!otherInputConfig.name && $dirts[otherInputConfig.name];
    const showCustomError = !!firstError && isDirty && $invalid;
    const currency = $params[otherSelectConfig.name!];

    return (
        <div className={classlist('currency-amount-root', className, { disabled: disabled })}>
            <div className={classlist('currency-amount-wrap', { 'is-invalid': showCustomError })}>
                <FormItem<number>
                    checker={(value, fieldProps) => {
                        if (Number(value) <= 0) {
                            return __('请输入金额');
                        }

                        if (!isPrice(value)) {
                            return __('只能输入整数或两位小数数字');
                        }

                        if (typeof checker === 'function') {
                            return checker(value, fieldProps);
                        }

                        return true;
                    }}
                    $formatter={$modelValue => ($modelValue ? formatPrice($modelValue) : '')}
                    $parser={($viewValue, $setViewValue) => {
                        $setViewValue($viewValue.replace(/[^0-9,.]/g, ''));

                        return !!$viewValue ? Number($viewValue.replace(/[^0-9.]/g, '')) : $viewValue;
                    }}
                    onBlur={() => {
                        const inputFormutil = $getField(otherInputConfig.name!);

                        if (inputFormutil && !inputFormutil?.$invalid) {
                            inputFormutil.$render(formatPrice($params[otherInputConfig.name!]));
                        }
                    }}
                    errorLevel={errorLevel}
                    {...otherInputConfig}
                    className={classlist('select-amount', inputClassName)}>
                    <Input disabled={disabled} placeholder={placeholder} />
                </FormItem>
                <div className="select-currency">
                    <FormItem
                        {...otherSelectConfig}
                        errorLevel="off"
                        className={classlist('select-currency-input', selectClassName)}>
                        <FieldSelect
                            disabled={disabled || selectDisabled}
                            showSearch={APP.isTiger ? showSearch : false}
                            renderInComponent
                            data={currencyList}
                            searchPlaceholder={__('Type a currency / country')}
                            label={__('Select Currency')}
                        />
                    </FormItem>
                    <div className={classlist('substitute-selector', { 'hide-icon': !showIcon })}>
                        <img className="icon-currency" src={getCurrencyInfo(currency).icon} alt={currency} />
                        {currency}
                        {showIcon && (
                            <img className="selector-icon" src={selectDisabled ? iconLock : arrowBottom} alt="" />
                        )}
                    </div>
                </div>
            </div>
            <Collapse in={Boolean(extra)}>{<>{extra}</>}</Collapse>
            <Collapse in={errorLevel === 'off' && !!showExplain && (showCustomError || !!help)}>
                <div
                    className={classlist(
                        'currency-amount-explain',
                        showCustomError ? 'explain-error' : 'explain-help'
                    )}>
                    {showCustomError ? firstError : help}
                </div>
            </Collapse>
        </div>
    );
};

export default connect(CurrencyAmount);
